import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { HyroTable } from "@hyro-components";
import HyroIconButton from "@hyro-components/hyro-icon-button/HyroIconButton";
import { ICON_BUTTON_SIZES, ICON_BUTTON_VARIANTS, ICON_COLOR_MODES } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";
import ExternalLink from "src/icons/ExternalLink";
import useAppStore from "src/stores/appStore";
import { defaultCollectionsLanguagesSelector } from "src/stores/selectors/collectionsSelectors";
import { useSchemaIntrospection } from "src/queries/hooks/knowledgeExplore";
import RefCell from "src/components/collections/knowledge-sources/explore/cell-renderers/RefCell";
import { isRefField } from "src/components/collections/knowledge-sources/explore/ExploreTab.utils";
import ChangeBadge from "./ChangeBadge";
import { RecordValueCell, ValueBox } from "./ChangeCells";
import { BADGE_COLUMN_WIDTH, CHANGE_TYPES, HEADER_CHAR_WIDTH, HEADER_PADDING, LINK_COLUMN_WIDTH, RECORD_COLUMN_MIN_WIDTH, REVIEW_COPY, SECTION_KINDS } from "./reviewChanges.consts";
import { FieldLabel, FieldRow, SectionTitle } from "./ReviewChangesDialog.styles";

const OpenLink = ({ link }) => (link ? (
  <HyroIconButton
    variant={ICON_BUTTON_VARIANTS.CLEAR}
    size={ICON_BUTTON_SIZES.SMALL}
    // The icon is a filled shape; the default stroke mode would outline it and make it look bold.
    iconColorMode={ICON_COLOR_MODES.FILL}
    // A new tab: the review (and a build about to start) stays where it is.
    component={RouterLink}
    to={link}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={REVIEW_COPY.OPEN_TAB}
    tooltip={REVIEW_COPY.OPEN_TAB}
  >
    <ExternalLink />
  </HyroIconButton>
) : null);

// "Field   [WAS before] → [NOW after]   ↗": an added value has no WAS, a removed one no NOW.
const FieldChange = ({ row, link }) => (
  <FieldRow data-testid={`field-change-${row.key}`}>
    <FieldLabel>{row.label}</FieldLabel>
    <ValueBox label={REVIEW_COPY.WAS} text={row.before} segments={row.segments} />
    <ArrowForwardIcon fontSize="small" color="disabled" />
    <ValueBox current label={REVIEW_COPY.NOW} text={row.after} segments={row.segments} />
    <OpenLink link={link} />
  </FieldRow>
);

const LINK_COLUMN_FIELD = "actions";

const recordColumns = ({ columns, link, refFields = {}, renderRef }) => [
  {
    field: "__change",
    headerName: REVIEW_COPY.COLUMNS.CHANGE,
    width: BADGE_COLUMN_WIDTH,
    renderCell: ({ row }) => <ChangeBadge changeType={row.changeType} />
  },
  ...columns.map(({ key, label }) => ({
    field: key,
    headerName: label,
    width: Math.max(RECORD_COLUMN_MIN_WIDTH, (label.length * HEADER_CHAR_WIDTH) + HEADER_PADDING),
    flex: 1,
    renderCell: ({ row }) => (
      <RecordValueCell
        cell={row.cells.find((cell) => cell.key === key)}
        renderStored={refFields[key] ? (value, isBefore) => renderRef(refFields[key], value, isBefore) : null}
        stored={row.data?.[key]}
        previousStored={row.previousData?.[key]}
        storedIsBefore={row.changeType === CHANGE_TYPES.REMOVED}
      />
    )
  })),
  // "actions" is the column HyroTable pins to its right edge, so the link stays visible when the table scrolls.
  {
    field: LINK_COLUMN_FIELD,
    headerName: "",
    width: LINK_COLUMN_WIDTH,
    renderCell: () => <OpenLink link={link} />
  }
];

// The changed rows of a table field or knowledge table.
const RecordTable = ({ section, link, testId, refFields, renderRef }) => (
  <HyroTable
    testId={`${testId}-table`}
    columns={recordColumns({ columns: section.columns, link, refFields, renderRef })}
    rows={section.rows.map((row) => ({ ...row, id: row.key }))}
    truncateCellText={false}
    overrideMaxRowHeight
    disableColumnSorting
    disableHover
    minHeight={1}
    dataGridProps={{ autoHeight: true }}
  />
);

// A knowledge table: its reference columns (a site, a route, a skill...) show the referenced item's label with
// Explore's own cell — resolved at the live version for what was there before (a removed row, the WAS side of an
// edit; the draft may have deleted the referenced item), at the draft's version otherwise.
const KnowledgeRecordTable = ({ section, link, testId }) => {
  const { data: introspection } = useSchemaIntrospection(section.schemaType);
  const defaultLanguage = useAppStore(defaultCollectionsLanguagesSelector);
  const refFields = Object.fromEntries((introspection?.__type?.fields ?? []).filter(isRefField).map((field) => [field.name, field]));
  const renderRef = (field, value, isBefore) => (
    <RefCell
      field={field}
      value={value}
      version={isBefore ? section.baseVersion : section.targetVersion}
      defaultLanguage={defaultLanguage}
      isEditable={false}
    />
  );
  return <RecordTable section={section} link={link} testId={testId} refFields={refFields} renderRef={renderRef} />;
};

// One part of a tab: field changes ("field: was → now", a nested field under its parents' path) or the changed
// rows of a table.
const ReviewSection = ({ section, link, testId }) => (
  <Stack gap={1.5} data-testid={testId}>
    {section.title && <SectionTitle>{section.title}</SectionTitle>}
    {section.kind === SECTION_KINDS.RECORDS
      ? (section.schemaType
        ? <KnowledgeRecordTable section={section} link={link} testId={testId} />
        : <RecordTable section={section} link={link} testId={testId} />)
      : section.rows.map((row, index) => (
        <React.Fragment key={row.key}>
          {row.group && row.group !== section.rows[index - 1]?.group && (
            <Typography variant="body2" fontWeight={500} color="text.700" sx={{ pt: index ? 1 : 0, wordBreak: "break-word" }}>{row.group}</Typography>
          )}
          <FieldChange row={row} link={link} />
        </React.Fragment>
      ))}
    {section.truncatedCount > 0 && (
      <Typography variant="body2" color="text.700">{REVIEW_COPY.moreRows(section.truncatedCount)}</Typography>
    )}
  </Stack>
);

export default ReviewSection;
